import { Layout } from '@/Layout';
import { BackLink } from '@/components/BackLink';
import {
  FetchImportExportHistoriesArgs,
  useIsImportExportHistoriesListFetching,
} from '@/context/services/import_export_histories/ImportExportHistories.service';
import { ErrorDetailDrawer } from '@/features/import_export_histories/Drawer';
import { Search } from '@/features/import_export_histories/Search';
import { ImportExportHistoriesTableView } from '@/features/import_export_histories/Table';
import { useCancelImportExportHistoryClick } from '@/features/import_export_histories/Table/hooks/useCancelImportExportHistoryClick';
import { useImportExportHistoriesPolling } from '@/features/import_export_histories/Table/hooks/useImportExportHistoriesPolling';
import {
  useImportExportHistoriesSearchQuery,
  useImportExportHistoriesSearchQueryInitial,
} from '@/features/import_export_histories/Table/hooks/useImportExportHistoriesSearchQuery';
import { useExportOptionsToast } from '@/features/import_export_histories/hooks/useExportOptionsToast';
import { useImportOptionsToast } from '@/features/import_export_histories/hooks/useImportOptionsToast';
import { useDownload } from '@/hooks/useDownload';
import { useTranslation } from '@/i18n';
import { Loading, useToastAPI } from '@moneyforward/ap-frontend-components';
import { useDownloadImportExportHistory } from 'ap-openapi';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useState } from 'react';
import styles from './ImportExportHistories.module.scss';

const cx = classnames.bind(styles);

type Props = {
  location: {
    state?: ImportExportHistoryLocationState;
  };
  searchParams: URLSearchParams;
};

type ImportExportHistoryLocationState = { from?: string };

export const ImportExportHistories: FC<Props> = memo((props: Props) => {
  const { t } = useTranslation();
  const notify = useToastAPI();

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const { location, searchParams } = props;
  const [from] = useState(location.state?.from);
  const initialSearchQuery =
    useImportExportHistoriesSearchQueryInitial(searchParams);

  const [searchQuery, setSearchQuery] =
    useState<FetchImportExportHistoriesArgs>(initialSearchQuery);

  const { handleSearch } = useImportExportHistoriesSearchQuery();
  const isListFetching = useIsImportExportHistoriesListFetching(searchQuery);
  const { onCancelClick, isCancelLoading } =
    useCancelImportExportHistoryClick();
  const { mutateAsync: downloadImportExportHistoryMutation } =
    useDownloadImportExportHistory();
  const download = useDownload();

  const { isPollingEnabled, startPolling, resetPollingTimer } =
    useImportExportHistoriesPolling();

  const isPageLoading =
    isCancelLoading || (!isPollingEnabled && isListFetching);

  useImportOptionsToast(startPolling);
  useExportOptionsToast(startPolling);

  const updateSearchQuery = useCallback(
    (updates: Partial<FetchImportExportHistoriesArgs>) => {
      setSearchQuery((prev: FetchImportExportHistoriesArgs) => {
        const newQuery = { ...prev, ...updates };
        handleSearch(newQuery);
        return newQuery;
      });
      // Restart the 5-min timer so polling continues with new search conditions
      if (isPollingEnabled) resetPollingTimer();
    },
    [handleSearch, isPollingEnabled, resetPollingTimer]
  );

  const downloadFile = useCallback(
    async (fileId: string, fileName?: string) => {
      const { data } = await downloadImportExportHistoryMutation(
        {
          id: fileId,
        },
        {
          onError: (error) => {
            notify.error({
              title: error?.messages?.join('\n') || t('system_error'),
              duration: 5,
            });
          },
        }
      );
      download({ value: data, fileName: fileName || `${fileName}.csv` });
    },
    [downloadImportExportHistoryMutation, download, notify, t]
  );

  return (
    <Layout
      title={t('import_export_history_page_title')}
      backLink={
        from ? (
          <BackLink href={from} text={t('import_export_history_back_link')} />
        ) : undefined
      }
    >
      {isCancelLoading && <Loading size='xlarge' isOverlay color='white' />}
      <div className={cx('container')}>
        <p className={cx('description')}>
          {t('import_export_history_description')}
        </p>
        <Search onSearch={updateSearchQuery} isLoading={isPageLoading} />
        <ImportExportHistoriesTableView
          searchQuery={searchQuery}
          onUpdateSearchQuery={updateSearchQuery}
          isPageLoading={isPageLoading}
          onCancelClick={onCancelClick}
          onDetailsClick={setSelectedId}
          onDownloadClick={downloadFile}
          isPollingEnabled={isPollingEnabled}
        />
        <ErrorDetailDrawer
          selectedId={selectedId}
          setSelectedId={setSelectedId}
        />
      </div>
    </Layout>
  );
});
ImportExportHistories.displayName = 'ImportExportHistories';
